import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BASE_CURRENCY, MAX_PURCHASE_ITEMS, validatePurchaseInput, type AssetCategory, type AssetInput, type PurchaseInput } from "@torre/assets";

import Modal from "../../../components/base/Modal";
import { Button, Input, Select } from "../../../components/ui";
import { useActivosMutation, useSuppliers } from "../hooks/useActivos";
import { handleSaveError } from "../lib/conflicts";
import { fieldErrors, optionalText, parseAmount } from "../lib/format";
import { categoryOptions, langOf } from "../lib/vocab";
import { activosApi, type PurchaseRow } from "../services/activosApi";
import { FormBlock, FormFooter } from "./shared";

// ============================================================================
// Alta y edición de una compra (una compra = una factura, D3). En el alta se
// capturan sus piezas y el API crea los N activos en una sola transacción.
// Archivo común, idéntico en CC-CONSOLE y WAYAK. Los montos y el tipo de cambio
// sólo con `feat:activos.costos` (D6). La integración con Finanzas llega en el
// PR 4: mientras, la compra con monto queda «Pendiente».
// ============================================================================

interface ItemState {
  name: string;
  category: string;
  serial: string;
  brand: string;
  model: string;
  unitCost: string;
}

const emptyItem = (): ItemState => ({ name: "", category: "", serial: "", brand: "", model: "", unitCost: "" });
const str = (v: unknown) => (v == null ? "" : String(v));

export function PurchaseFormModal({
  purchase,
  canSeeCosts,
  onClose,
  onOpenPurchase,
}: {
  /** Con compra, edición (sin piezas); sin ella, alta con piezas. */
  purchase?: PurchaseRow | null;
  canSeeCosts: boolean;
  onClose: () => void;
  /** Abre otra compra (409 `COMPRA_DUPLICADA`). */
  onOpenPurchase?: (purchaseId: string) => void;
}) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const isEdit = Boolean(purchase?.id);
  const suppliers = useSuppliers({});
  const [supplierId, setSupplierId] = useState(str(purchase?.supplierId));
  const [date, setDate] = useState(str(purchase?.date));
  const [uuid, setUuid] = useState(str(purchase?.invoice?.uuid));
  const [serie, setSerie] = useState(str(purchase?.invoice?.serie));
  const [folio, setFolio] = useState(str(purchase?.invoice?.folio));
  const [currency, setCurrency] = useState(purchase?.currency || BASE_CURRENCY);
  const [exchangeRate, setExchangeRate] = useState(str(purchase?.exchangeRate));
  const [subtotal, setSubtotal] = useState(str(purchase?.subtotal));
  const [tax, setTax] = useState(str(purchase?.tax));
  const [total, setTotal] = useState(str(purchase?.total));
  const [items, setItems] = useState<ItemState[]>(() => (isEdit ? [] : [emptyItem()]));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const uuidLocked = isEdit && Boolean(purchase?.invoice?.uuid);

  const save = useActivosMutation((input: PurchaseInput) => (isEdit ? activosApi.updatePurchase(purchase!.id, input) : activosApi.createPurchase(input)));
  const supplierOptions = useMemo(
    () => (suppliers.data?.items || []).map((s) => ({ value: s.id, label: s.rfc ? `${s.name} · ${s.rfc}` : s.name })),
    [suppliers.data],
  );

  const setItem = (i: number, k: keyof ItemState, v: string) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const invoice = uuid.trim() || serie.trim() || folio.trim() ? { uuid: optionalText(uuid), serie: optionalText(serie), folio: optionalText(folio) } : null;
    const payload: PurchaseInput = {
      supplierId: optionalText(supplierId),
      date: optionalText(date),
      invoice,
      currency: optionalText(currency) || BASE_CURRENCY,
    };
    if (canSeeCosts) {
      payload.exchangeRate = parseAmount(exchangeRate);
      payload.subtotal = parseAmount(subtotal);
      payload.tax = parseAmount(tax);
      payload.total = parseAmount(total);
    }
    if (!isEdit) {
      payload.items = items.map((it): AssetInput => ({
        name: it.name,
        category: (it.category || undefined) as AssetCategory | undefined,
        serial: optionalText(it.serial),
        brand: optionalText(it.brand),
        model: optionalText(it.model),
        ...(canSeeCosts ? { purchase: { unitCost: parseAmount(it.unitCost) } } : {}),
      }));
    }
    const checked = validatePurchaseInput(payload, { mode: isEdit ? "update" : "create", canSeeCosts });
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    try {
      await save.mutateAsync(payload);
      toast.success(t(isEdit ? "purchaseForm.updated" : "purchaseForm.created"));
      onClose();
    } catch (err) {
      const outcome = await handleSaveError(err, t, { navigate, openPurchase: onOpenPurchase });
      if (outcome.kind === "fields") setErrors(fieldErrors(outcome.errors, lang));
      if (outcome.kind === "redirected") onClose();
    }
  };

  return (
    <Modal
      title={t(isEdit ? "purchaseForm.editTitle" : "purchaseForm.createTitle")}
      onClose={onClose}
      width="w-full max-w-4xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="purchase-form">
          <FormBlock title={t("purchaseForm.blocks.invoice")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label={t("fields.supplier")}
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                options={[{ value: "", label: t("common.none") }, ...supplierOptions]}
                error={errors.supplierId}
              />
              <Input label={t("fields.purchaseDate")} type="date" required value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} />
              <Input
                label={t("fields.invoiceUuid")}
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
                disabled={uuidLocked}
                hint={uuidLocked ? t("purchaseForm.uuidLocked") : t("purchaseForm.uuidHint")}
                error={errors["invoice.uuid"]}
                containerClassName="sm:col-span-2"
              />
              <Input label={t("fields.invoiceSerie")} value={serie} onChange={(e) => setSerie(e.target.value)} error={errors["invoice.serie"]} />
              <Input label={t("fields.invoiceFolio")} value={folio} onChange={(e) => setFolio(e.target.value)} error={errors["invoice.folio"]} />
              <Input label={t("fields.currency")} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value)} error={errors.currency} />
            </div>
          </FormBlock>

          {canSeeCosts ? (
            <FormBlock title={t("purchaseForm.blocks.amounts")}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4" data-testid="purchase-form-costs">
                <Input label={t("fields.subtotal")} inputMode="decimal" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} error={errors.subtotal} />
                <Input label={t("fields.tax")} inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} error={errors.tax} />
                <Input label={t("fields.total")} inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} error={errors.total} />
                <Input label={t("fields.exchangeRate")} inputMode="decimal" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} error={errors.exchangeRate} />
              </div>
              <p className="text-xs text-content-muted">{t("purchaseForm.financeNote")}</p>
            </FormBlock>
          ) : null}

          {!isEdit ? (
            <FormBlock title={t("purchaseForm.blocks.items", { count: items.length })}>
              {errors.items ? <p className="text-xs text-error">{errors.items}</p> : null}
              <div className="space-y-3">
                {items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-line p-3" data-testid={`purchase-item-${i}`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                      <Input label={t("fields.name")} required value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} error={errors[`items[${i}].name`]} containerClassName="sm:col-span-2" />
                      <Select
                        label={t("fields.category")}
                        required
                        value={it.category}
                        onChange={(e) => setItem(i, "category", e.target.value)}
                        options={categoryOptions(lang)}
                        placeholder={t("common.choose")}
                        error={errors[`items[${i}].category`]}
                        containerClassName="sm:col-span-2"
                      />
                      <Input label={t("fields.serial")} value={it.serial} onChange={(e) => setItem(i, "serial", e.target.value)} error={errors[`items[${i}].serial`]} containerClassName="sm:col-span-2" />
                      <Input label={t("fields.brand")} value={it.brand} onChange={(e) => setItem(i, "brand", e.target.value)} containerClassName="sm:col-span-2" />
                      <Input label={t("fields.model")} value={it.model} onChange={(e) => setItem(i, "model", e.target.value)} containerClassName="sm:col-span-2" />
                      {canSeeCosts ? (
                        <Input label={t("fields.unitCost")} inputMode="decimal" value={it.unitCost} onChange={(e) => setItem(i, "unitCost", e.target.value)} error={errors[`items[${i}].purchase.unitCost`]} />
                      ) : null}
                      <div className={`flex items-end ${canSeeCosts ? "" : "sm:col-span-2"}`}>
                        <Button
                          size="sm"
                          variant="dangerSoft"
                          iconOnly
                          aria-label={t("purchaseForm.removeItem")}
                          disabled={items.length === 1}
                          icon={<i className="ri-delete-bin-line" />}
                          onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                variant="dashed"
                icon={<i className="ri-add-line" />}
                disabled={items.length >= MAX_PURCHASE_ITEMS}
                onClick={() => setItems((prev) => [...prev, emptyItem()])}
                data-testid="purchase-add-item"
              >
                {t("purchaseForm.addItem")}
              </Button>
            </FormBlock>
          ) : null}

          <FormFooter
            onCancel={onClose}
            submitting={save.isPending}
            submitLabel={t(isEdit ? "common.save" : "purchaseForm.create")}
            submitTestId="purchase-form-submit"
          />
        </form>
      }
    />
  );
}
